/* eslint-disable @next/next/no-img-element */
import { useContext, useEffect, useState } from 'react';
import { Input } from '@acko-tech/building-blocks.ui.input';
import { PrimaryButton } from '@acko-tech/building-blocks.ui.button';
import { useRouter } from 'next/router';
import Header from 'widgets/header';
import Checkbox from 'library/checkbox';
import { mapFormConfigs } from 'components/login/helpers';
import { MPosContext } from 'utils/context';
import { FORM_TYPES } from 'utils/constants';
import { getImageBasePath } from 'utils/generics';
import { sanitizePartnerLogoUrl } from 'utils/helpers/partnerLogo';
import { useViewport } from 'utils/responsive';
import { PageLoader } from 'utils/styles';
import ErrorPage from 'widgets/errorScreen';
import * as Styles from './styles';
import { fetchServicePartnerData, saveServicePartnerData } from './apis';
import { serviceCenterOnboardingMockFormConfig } from './mockFormConfig';

function resolvePreviewSrc(src: string) {
    if (!src) {
        return '';
    }

    const sanitizedSrc = sanitizePartnerLogoUrl(src);

    if (sanitizedSrc.startsWith('http') || sanitizedSrc.startsWith('data:')) {
        return sanitizedSrc;
    }

    return getImageBasePath(sanitizedSrc);
}

function resolveTncSrc(src: string) {
    if (!src) {
        return '';
    }

    return sanitizePartnerLogoUrl(src);
}

function getSectionVariant(formType: string) {
    switch (formType) {
        case FORM_TYPES.SERVICE_CENTER_POC_DETAILS:
            return 'editable';
        case FORM_TYPES.SERVICE_CENTER_TNC:
            return 'tnc';
        default:
            return 'readonly';
    }
}

function mergeServicePartnerForms(baseForms: any[], incomingForms: any[]) {
    const incomingMap = incomingForms.reduce((acc, form) => {
        acc[form.form_type] = form;
        return acc;
    }, {});

    const mergedForms = baseForms.map((form) => {
        const incomingForm = incomingMap[form.form_type];

        if (!incomingForm) {
            return form;
        }

        return {
            ...form,
            ...incomingForm,
            ui_config: incomingForm.ui_config || form.ui_config,
            captured_data:
                incomingForm.captured_data === undefined
                    ? form.captured_data
                    : incomingForm.captured_data,
        };
    });

    const additionalForms = incomingForms.filter(
        (incomingForm) =>
            !baseForms.some((form) => form.form_type === incomingForm.form_type)
    );

    return [...mergedForms, ...additionalForms];
}

function buildInitialFormValues(forms: any[]) {
    return forms.reduce(
        (acc, form) => ({
            ...acc,
            ...(form.captured_data || {}),
        }),
        {}
    );
}

const servicePartnerFormTypes = new Set([
    FORM_TYPES.SERVICE_CENTER_DETAILS,
    FORM_TYPES.SERVICE_CENTER_PROFESSIONAL_DETAILS,
    FORM_TYPES.SERVICE_CENTER_BANK_DETAILS,
    FORM_TYPES.SERVICE_CENTER_POC_DETAILS,
    FORM_TYPES.SERVICE_CENTER_TNC,
]);

function buildServicePartnerFormsFromContext(
    formConfigsMap: Record<string, any>
) {
    return Object.entries(formConfigsMap)
        .filter(([formType]) => servicePartnerFormTypes.has(formType as any))
        .map(([formType, formConfig]) => ({
            form_type: formType,
            ui_config: formConfig?.uiConfig || [],
            captured_data: formConfig?.captured_data || {},
            should_capture_details: formConfig?.should_capture_details,
            details_update_required: formConfig?.details_update_required,
        }));
}

function ServiceCenterOnboarding() {
    const { isMobile } = useViewport();
    const router = useRouter();
    const { permissions, setSnackbar, formConfigsMap, setFormConfigsMap } =
        useContext(MPosContext);
    const [forms, setForms] = useState(serviceCenterOnboardingMockFormConfig);
    const formConfigMap = mapFormConfigs(forms);
    const [formValues, setFormValues] = useState<Record<string, any>>(
        buildInitialFormValues(serviceCenterOnboardingMockFormConfig)
    );
    const [hasScrolledTncToBottom, setHasScrolledTncToBottom] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');

    useEffect(() => {
        const partnerId = permissions?.partner_id;
        const groupName = permissions?.group;

        if (!partnerId || !groupName) {
            setIsLoading(false);
            return;
        }

        const loadServicePartnerConfig = async () => {
            const contextForms =
                buildServicePartnerFormsFromContext(formConfigsMap);
            let nextForms = mergeServicePartnerForms(
                serviceCenterOnboardingMockFormConfig,
                contextForms
            );

            const dataResponse = await fetchServicePartnerData(
                partnerId,
                groupName
            );

            if (dataResponse.statusCode === 401) {
                router.push('/login');
                return;
            }

            if (!dataResponse.is_error && dataResponse.data) {
                nextForms = mergeServicePartnerForms(
                    nextForms,
                    dataResponse.data
                );
                if (setFormConfigsMap) {
                    setFormConfigsMap((prev: Record<string, any>) => ({
                        ...prev,
                        ...mapFormConfigs(dataResponse.data || []),
                    }));
                }
            } else {
                setSnackbar({
                    type: 'failure',
                    message:
                        dataResponse.error_message ||
                        'Failed to fetch service partner data',
                });
            }

            setForms(nextForms);
            setFormValues(buildInitialFormValues(nextForms));
            setIsLoading(false);
        };

        loadServicePartnerConfig();
    }, [permissions?.partner_id, permissions?.group]);

    const handleFieldChange = (key: string) => (e: any) => {
        const value = e?.target?.value || '';

        setFormValues((prev) => ({
            ...prev,
            [key]: value,
        }));
    };

    const handleCheckboxToggle = (key: string) => () => {
        if (key === 'tnc_accepted' && !isMobile && !hasScrolledTncToBottom) {
            return;
        }

        const isTurningOff = Boolean(formValues[key]);
        const nextValue = !formValues[key];

        if (
            key === 'tnc_reveal' &&
            nextValue &&
            isMobile &&
            formValues.tnc_url
        ) {
            window.open(
                String(formValues.tnc_url),
                '_blank',
                'noopener,noreferrer'
            );
        }

        setFormValues((prev) => ({
            ...prev,
            [key]: nextValue,
            ...(key === 'tnc_reveal' && isTurningOff
                ? { tnc_accepted: false }
                : {}),
        }));

        if (key === 'tnc_reveal' && isTurningOff) {
            setHasScrolledTncToBottom(false);
        }
    };

    const handleTncScroll = (e: any) => {
        const target = e.currentTarget;
        const reachedBottom =
            target.scrollTop + target.clientHeight >= target.scrollHeight - 8;

        if (reachedBottom) {
            setHasScrolledTncToBottom(true);
        }
    };

    const isSubmitEnabled =
        Boolean(formValues.communication_name) &&
        Boolean(formValues.communication_phone) &&
        Boolean(formValues.tnc_reveal) &&
        Boolean(formValues.tnc_accepted);

    const handleSubmit = async () => {
        const partnerId = permissions?.partner_id;
        const groupName = permissions?.group;
        const currentLob = Array.isArray(router.query.slug)
            ? router.query.slug[0]
            : '';

        if (!partnerId || !groupName) {
            setErrorMessage('Partner information is missing.');
            return;
        }

        setIsSubmitting(true);

        const response = await saveServicePartnerData(partnerId, groupName, {
            service_center_poc_details: {
                communication_name: formValues.communication_name || '',
                communication_phone: formValues.communication_phone || '',
                communication_email: formValues.communication_email || '',
            },
            service_center_tnc: {
                tnc_reveal: !!formValues.tnc_reveal,
                tnc_accepted: !!formValues.tnc_accepted,
            },
        });

        if (response.statusCode === 401) {
            router.push('/login');
            return;
        }

        if (response.is_error) {
            setSnackbar({
                type: 'failure',
                message:
                    response.error_message ||
                    'Failed to save service partner details',
            });
            setIsSubmitting(false);
            return;
        }

        setSnackbar({
            type: 'success',
            message: 'Service partner details saved successfully',
        });
        setIsSubmitting(false);
        router.push(`/${currentLob || 'embedded'}/${partnerId}/dashboard`);
    };

    if (isLoading) {
        return <PageLoader src={getImageBasePath('/gif/pageLoader.gif')} />;
    }

    if (errorMessage) {
        return <ErrorPage errorMessage={errorMessage} />;
    }

    return (
        <Styles.Wrapper>
            <Header />
            <Styles.Content>
                <Styles.Section $index={0}>
                    <Styles.HeroCard>
                        <Styles.HeroEyebrow>
                            Service Partner Onboarding
                        </Styles.HeroEyebrow>
                        <Styles.Title>Service Partner</Styles.Title>
                        <Styles.Subtitle>
                            Review the prefilled partner information, complete
                            the POC details, and accept the terms to submit the
                            onboarding request.
                        </Styles.Subtitle>
                    </Styles.HeroCard>
                </Styles.Section>
                {forms.map((form, index) => {
                    const mappedConfig = formConfigMap[form.form_type];
                    const variant = getSectionVariant(form.form_type);

                    return (
                        <Styles.Section
                            $index={index + 1}
                            $variant={variant}
                            key={form.form_type}
                        >
                            <Styles.Card $variant={variant}>
                                <Styles.CardHeader>
                                    <Styles.SectionTitle>
                                        {form.section_title}
                                    </Styles.SectionTitle>
                                    <Styles.SectionBadge $variant={variant}>
                                        {variant === 'readonly'
                                            ? 'Prefilled'
                                            : variant === 'editable'
                                            ? 'Action needed'
                                            : 'Required'}
                                    </Styles.SectionBadge>
                                </Styles.CardHeader>
                                <Styles.FieldsGrid $variant={variant}>
                                    {mappedConfig?.uiConfig?.map((field) => {
                                        const key =
                                            field?.props?.key_name || field.id;
                                        const value = formValues[key] || '';

                                        if (
                                            form.form_type ===
                                            FORM_TYPES.SERVICE_CENTER_TNC
                                        ) {
                                            if (
                                                field.component ===
                                                'tnc_reveal_checkbox'
                                            ) {
                                                return (
                                                    <Styles.TncControlRow
                                                        key={field.id}
                                                    >
                                                        <Styles.CheckBoxFlex>
                                                            <Checkbox
                                                                isSelected={
                                                                    !!value
                                                                }
                                                                onSelectHandler={handleCheckboxToggle(
                                                                    key
                                                                )}
                                                                color={
                                                                    '#0FA457'
                                                                }
                                                                width={'18px'}
                                                                height={'18px'}
                                                            />
                                                        </Styles.CheckBoxFlex>
                                                        <Styles.TncText>
                                                            {field.label}
                                                        </Styles.TncText>
                                                    </Styles.TncControlRow>
                                                );
                                            }

                                            if (
                                                field.component ===
                                                    'tnc_accept_checkbox' &&
                                                formValues.tnc_reveal
                                            ) {
                                                return (
                                                    <Styles.TncControlRow
                                                        key={field.id}
                                                    >
                                                        <Styles.CheckBoxFlex>
                                                            <Checkbox
                                                                isSelected={
                                                                    !!value
                                                                }
                                                                onSelectHandler={handleCheckboxToggle(
                                                                    key
                                                                )}
                                                                color={
                                                                    '#0FA457'
                                                                }
                                                                width={'18px'}
                                                                height={'18px'}
                                                            />
                                                        </Styles.CheckBoxFlex>
                                                        <Styles.TncText
                                                            $disabled={
                                                                !isMobile &&
                                                                !hasScrolledTncToBottom
                                                            }
                                                        >
                                                            {field.label}
                                                        </Styles.TncText>
                                                    </Styles.TncControlRow>
                                                );
                                            }

                                            if (
                                                field.component ===
                                                    'tnc_preview' &&
                                                formValues.tnc_reveal &&
                                                !isMobile
                                            ) {
                                                return (
                                                    <Styles.TncPreviewSection
                                                        key={field.id}
                                                    >
                                                        <Styles.TncPreviewCard
                                                            onScroll={
                                                                handleTncScroll
                                                            }
                                                        >
                                                            <iframe
                                                                src={resolveTncSrc(
                                                                    String(
                                                                        value
                                                                    )
                                                                )}
                                                                title={
                                                                    'Terms and conditions'
                                                                }
                                                            />
                                                        </Styles.TncPreviewCard>
                                                        <Styles.TncHint>
                                                            Scroll to the bottom
                                                            to enable
                                                            acceptance.
                                                        </Styles.TncHint>
                                                    </Styles.TncPreviewSection>
                                                );
                                            }
                                        }

                                        if (
                                            field.component ===
                                                'image_preview' &&
                                            value
                                        ) {
                                            return (
                                                <Styles.PreviewWrapper
                                                    key={field.id}
                                                    $fullWidth={
                                                        variant !== 'readonly'
                                                    }
                                                >
                                                    <Styles.PreviewLabel>
                                                        {field.label}
                                                    </Styles.PreviewLabel>
                                                    <Styles.PreviewCard>
                                                        <img
                                                            src={resolvePreviewSrc(
                                                                value
                                                            )}
                                                            alt={field.label}
                                                        />
                                                    </Styles.PreviewCard>
                                                </Styles.PreviewWrapper>
                                            );
                                        }

                                        return (
                                            <Styles.InputWrapper
                                                key={field.id}
                                                $fullWidth={
                                                    field.type !== 'text' &&
                                                    field.type !== 'email' &&
                                                    field.type !== 'tel'
                                                }
                                            >
                                                <Input
                                                    className={''}
                                                    labelText={field.label}
                                                    name={key}
                                                    type={field.type}
                                                    value={value}
                                                    placeHolder={field.title}
                                                    onChange={handleFieldChange(
                                                        key
                                                    )}
                                                    disabled={
                                                        field.props
                                                            ?.editable === false
                                                    }
                                                />
                                            </Styles.InputWrapper>
                                        );
                                    })}
                                </Styles.FieldsGrid>
                                {form.form_type ===
                                FORM_TYPES.SERVICE_CENTER_TNC ? (
                                    <Styles.SubmitButtonWrapper>
                                        <PrimaryButton
                                            disabled={!isSubmitEnabled}
                                            loading={isSubmitting}
                                            onClick={handleSubmit}
                                        >
                                            Submit
                                        </PrimaryButton>
                                    </Styles.SubmitButtonWrapper>
                                ) : null}
                            </Styles.Card>
                        </Styles.Section>
                    );
                })}
            </Styles.Content>
        </Styles.Wrapper>
    );
}

export default ServiceCenterOnboarding;
